import { useState } from 'react';
import type { MetricWindow, ServerView } from '../api/types';
import { api } from '../api/client';
import { LineChart } from '../components/LineChart';
import { MetricWindowPicker, windowPollMs } from '../components/MetricWindowPicker';
import { Empty, ErrorAlert, Meter } from '../components/ui';
import { formatGb, formatRelative, percent, softwareLabel, tpsTone } from '../format';
import { usePoll } from '../hooks/usePoll';

function Metrics({ server }: { server: ServerView }) {
  const [period, setPeriod] = useState<MetricWindow>('1h');
  const metrics = usePoll(() => api.metrics(server.id, period), windowPollMs(period), [
    server.id,
    JSON.stringify(period),
  ]);
  const points = metrics.data?.points ?? [];
  const times = points.map((p) => p.t);
  const zoom = (from: string, to: string) => setPeriod({ from, to });
  return (
    <section className="stack" style={{ gap: 10 }}>
      <h3>推移</h3>
      <MetricWindowPicker value={period} onChange={setPeriod} />
      <ErrorAlert error={metrics.error} />
      {points.length === 0 && !metrics.loading ? (
        <Empty>{typeof period === 'string' ? 'まだ監視値がありません。' : 'この期間の監視値はありません。'}</Empty>
      ) : (
        <div className="grid grid-charts">
          <div className="card card-body">
            <h3>CPU</h3>
            <LineChart
              times={times}
              onSelect={zoom}
              series={[{ label: 'CPU', values: points.map((p) => p.cpu), color: 'var(--chart-1)' }]}
              min={0}
              max={1}
              format={(v) => `${Math.round(v * 100)}%`}
            />
          </div>
          <div className="card card-body">
            <h3>メモリ</h3>
            <LineChart
              times={times}
              onSelect={zoom}
              series={[{ label: 'メモリ', values: points.map((p) => p.memoryMb), color: 'var(--chart-1)' }]}
              min={0}
              max={server.resources.memoryMb}
              format={(v) => formatGb(v)}
            />
          </div>
          <div className="card card-body">
            <h3>プレイヤー</h3>
            <LineChart
              times={times}
              onSelect={zoom}
              series={[{ label: 'プレイヤー', values: points.map((p) => p.players), color: 'var(--chart-2)' }]}
              min={0}
              format={(v) => String(Math.round(v))}
            />
          </div>
          <div className="card card-body">
            <h3>MSPT（50ms を超えると TPS が落ちる）</h3>
            <LineChart
              times={times}
              onSelect={zoom}
              series={[{ label: 'MSPT', values: points.map((p) => p.mspt), color: 'var(--chart-2)' }]}
              min={0}
              max={Math.max(60, ...points.map((p) => p.mspt ?? 0))}
              format={(v) => `${v.toFixed(0)}ms`}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export function ServerOverview({ server }: { server: ServerView }) {
  const live = server.live;
  const running = server.state === 'running';
  return (
    <div className="stack" style={{ gap: 20 }}>
      <dl className="overview-strip">
        <div className="overview-column">
          <dt className="overview-heading">現在</dt>
          <dd className="overview-content stack">
            <dl className="overview-values">
              <dt>プレイヤー</dt>
              <dd className="numeric">{running && live ? `${live.players}/${live.maxPlayers}` : '—'}</dd>
              <dt>TPS</dt>
              <dd className="numeric">
                <span className={live?.tps !== undefined && live.tps < 19 ? `metric-${tpsTone(live.tps)}` : undefined}>
                  {running && live?.tps !== undefined ? live.tps.toFixed(1) : '—'}
                </span>
              </dd>
              <dt>MSPT</dt>
              <dd className="numeric">{running && live?.mspt !== undefined ? `${live.mspt.toFixed(1)} ms` : '—'}</dd>
            </dl>
            {running && live?.playerNames && live.playerNames.length > 0 && (
              <div className="small muted">{live.playerNames.join('、')}</div>
            )}
            {live && <div className="small faint">更新 {formatRelative(live.updatedAt)}</div>}
          </dd>
        </div>
        <div className="overview-column">
          <dt className="overview-heading">リソース</dt>
          <dd className="overview-content stack">
            <Meter
              label="CPU"
              value={running ? (live?.cpu ?? 0) : 0}
              max={1}
              detail={`${running ? percent(live?.cpu) : '—'} ・ ${server.resources.cores} コア`}
            />
            <Meter
              label="メモリ"
              neutral
              value={running ? (live?.memoryUsedMb ?? 0) : 0}
              max={server.resources.memoryMb}
              detail={`${running && live?.memoryUsedMb !== undefined ? formatGb(live.memoryUsedMb) : '—'} / ${formatGb(server.resources.memoryMb)}（ヒープ ${formatGb(server.resources.heapMb)}）`}
            />
            <Meter
              label="データディスク"
              value={live?.dataDiskUsedGb ?? 0}
              max={server.resources.dataDiskGb}
              detail={`${live?.dataDiskUsedGb !== undefined ? `${live.dataDiskUsedGb.toFixed(1)}GB` : '—'} / ${server.resources.dataDiskGb}GB`}
            />
          </dd>
        </div>
        <div className="overview-column">
          <dt className="overview-heading">構成</dt>
          <dd className="overview-content stack">
            <dl
              className="small"
              style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}
            >
              <dt className="faint">ソフト</dt>
              <dd style={{ margin: 0 }}>
                {softwareLabel[server.software.family]} {server.software.mcVersion}
                {server.software.build && <span className="faint"> build {server.software.build}</span>}
                {server.software.loaderVersion && (
                  <span className="faint"> loader {server.software.loaderVersion}</span>
                )}
              </dd>
              <dt className="faint">ノード</dt>
              <dd style={{ margin: 0 }}>
                {server.node ?? '—'} {server.vmid && <span className="faint">VMID {server.vmid}</span>}
              </dd>
              <dt className="faint">接続</dt>
              <dd style={{ margin: 0 }}>
                {server.proxied ? 'Velocity 配下（まず hub に入り、/server で移動）' : '直接接続'}
              </dd>
              <dt className="faint">HA</dt>
              <dd style={{ margin: 0 }}>{server.ha ? '有効' : '無効'}</dd>
            </dl>
          </dd>
        </div>
      </dl>
      <Metrics server={server} />
    </div>
  );
}
